import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { reasonMessage, type SupportedLanguage } from '../../i18n';

import { numericInputSx, OptionText, SettingsSection, SwitchRow } from './SettingsSection';
import type { CurrentDailyLimit, DailyLimitDraft, LimitsAccount } from './daily-limit';
import { formatBpsPercent } from './display';
import { type DraftIssue, hasInvalid, type SettingsDraft } from './settings-draft';

/** Props of {@link LimitsSection}. */
interface LimitsSectionProps {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
  readonly issues: readonly DraftIssue[];
  readonly account: LimitsAccount;
  readonly dailyLimit: DailyLimitDraft;
  readonly onDailyLimitChange: (draft: DailyLimitDraft) => void;
  /** A typed amount that is wrong (empty-but-required shows in the list under Save). */
  readonly dailyLimitInvalid: boolean;
  /** A typed amount with no digit before its separator (",5"): incomplete, with a hint. */
  readonly dailyLimitLeadingDigit: boolean;
}

/**
 * "Safety limits · recommended" card (M2 R11, R12, AC14). Both limits are off unless saved (I7).
 * The daily loss is account-wide and saved apart from the channel (`PUT /account/limits`).
 */
export function LimitsSection(props: LimitsSectionProps): ReactElement {
  const { t } = useTranslation();
  const { draft, onChange, issues } = props;
  const maxInvalid = hasInvalid(issues, 'maxOpenPositions');
  return (
    <SettingsSection title={t('settings.limits.title')}>
      <SwitchRow
        checked={draft.maxOpenPositionsEnabled}
        onChange={(checked) => {
          onChange({ maxOpenPositionsEnabled: checked });
        }}
        label={<OptionText title={t('settings.limits.maxPositions')} />}
      />
      {draft.maxOpenPositionsEnabled && (
        <TextField
          label={t('settings.limits.maxPositionsLabel')}
          value={draft.maxOpenPositions}
          onChange={(event) => {
            onChange({ maxOpenPositions: event.target.value });
          }}
          error={maxInvalid}
          helperText={maxInvalid ? t('settings.errors.maxOpenPositions') : undefined}
          slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
          sx={numericInputSx}
        />
      )}
      <Box
        sx={(theme) => ({
          pt: 1.5,
          borderTop: `1px solid ${theme.fx.neutralBg}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        })}
      >
        <DailyLimit {...props} />
      </Box>
    </SettingsSection>
  );
}

/**
 * The account-wide daily-loss limit, in % of the capital (FXM-02 §4). C1 settled the unit
 * (R6, 14/10): the card shows the saved limit in % and takes a new one in % with at most two
 * decimals, which `validateDailyLimitDraft` sends as integer basis points (M2 R12).
 */
function DailyLimit({
  account,
  dailyLimit,
  onDailyLimitChange,
  dailyLimitInvalid,
  dailyLimitLeadingDigit,
}: LimitsSectionProps): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const scope = (
    <>
      {t('settings.limits.dailyLossScope')} {t('settings.limits.allChannels')}
    </>
  );
  if (account.kind === 'unavailable') {
    // The limit cannot be read, so the switch is not drawn as on or off (I7).
    const reason =
      account.reasonCode === 'DAILY_LOSS_NOT_AVAILABLE'
        ? t('settings.limits.dailyLossNotAvailable')
        : (() => {
            const message = reasonMessage(account.reasonCode);
            return t(message.key, message.values);
          })();
    return (
      <OptionText
        title={t('settings.limits.dailyLoss')}
        hint={
          <>
            {scope} {reason}
          </>
        }
      />
    );
  }
  const current = account.current;
  return (
    <>
      <SwitchRow
        checked={dailyLimit.enabled}
        onChange={(enabled) => {
          onDailyLimitChange({ ...dailyLimit, enabled });
        }}
        label={<OptionText title={t('settings.limits.dailyLoss')} hint={scope} />}
      />
      {dailyLimit.enabled && current !== null && (
        <Box component="p" sx={{ m: 0, fontSize: 13 }}>
          <CurrentLimit current={current} language={language} />
        </Box>
      )}
      {dailyLimit.enabled && (
        <TextField
          label={t('settings.limits.amountLabel')}
          value={dailyLimit.amount}
          onChange={(event) => {
            onDailyLimitChange({ ...dailyLimit, amount: event.target.value });
          }}
          error={dailyLimitInvalid}
          helperText={
            dailyLimitInvalid
              ? t('settings.errors.dailyLoss')
              : dailyLimitLeadingDigit
                ? t('settings.errors.leadingDigit')
                : current === null
                  ? undefined
                  : t('settings.limits.keepCurrent')
          }
          slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
          sx={numericInputSx}
        />
      )}
    </>
  );
}

/** The saved limit, its number in NUMERIC_FONT (master UI rule). */
function CurrentLimit({
  current,
  language,
}: {
  readonly current: CurrentDailyLimit;
  readonly language: SupportedLanguage;
}): ReactElement {
  return (
    <Trans
      i18nKey="settings.limits.currentPercent"
      values={{ value: formatBpsPercent(current.bps, language) }}
      components={{ num: <Box component="span" sx={{ fontFamily: NUMERIC_FONT }} /> }}
    />
  );
}
